"use client";

import { Table } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2,
  Plus,
  Search,
  Upload,
} from "lucide-react";
import { DataTableViewOptions } from "./data-table-view-options";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  SelectItem,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDispatch } from "react-redux";
import {
  setActivePallet,
  setActiveWeek,
  setPalletTypes
} from "@/redux/slices/pallet/palletSlice";
import { setDefineData } from "@/redux/slices/define/defineSlice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import ModalContent from './modal/ModalContent';
import { importThreeDxProject, type ThreeDxProjectResponse } from '@/services/ThreeDxProjectService';
import ConfirmDialog from './modal/ConfirmDialog';
import { showErrorToast, showSuccessToast } from '@/components/toast/toast';
import { toast } from 'sonner';


interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  isOffline?: boolean;
  isDistribution?: boolean;
  serverPagination?: boolean;
  totalPages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function DataTableToolbar<TData>({
  table,
  isDistribution = false,
  isOffline = false,
  serverPagination = false,
  totalPages = 1,
  currentPage = 1,
  onPageChange,
  searchValue = '',
  onSearchChange,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [modalSearchValue, setModalSearchValue] = useState("");
  const [apiData, setApiData] = useState<ThreeDxProjectResponse | null>(null);
  const [errorToastId, setErrorToastId] = useState<string | number | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const importedProject = apiData?.data?.[0];


  const handleCreateProject = () => {
    setShowDialog(true);
  };

  const handleConfirmCreate = () => {
    setLoading(true);
    dispatch(setPalletTypes([]));
    dispatch(setActivePallet(null));
    dispatch(setActiveWeek(null));
    dispatch(setDefineData({
      type: 'create',
      projectId: undefined,
      target: 2,
      projectDetails: {
        projectName: '',
        projectDescription: '',
      },
      shippingModes: [
        {
          mode: 1,
          transitMode: "",
          startCity: "",
          startStateName: "",
          startZip: "",
          endCity: "",
          endStateName: "",
          endZip: "",
          distanceTravelled: "",
          elevationChanges: "",
          elevationMax: "",
          intermediateStopNotes: "",
        }
      ],
    }));
    navigate(isOffline ? "/offline/projects/new-project" : "/projects/new-project");
  };

  const handleImportButtonClick = async () => {
    const project = apiData?.data?.[0];
    const projectID = project?.project_info?.projectID;
   
    if (!project) {
      showErrorToast("No 3DX project is available to import");
      return;
    }

    setIsImporting(true);
    try {
      const { projectId: importedProjectId, message } = await importThreeDxProject(project);
      showSuccessToast("Project imported successfully! ", {
        description: message || undefined,
        width: "700px",
        duration: 5000,
        showCloseButton: true,
      });
      setShowImportConfirm(false);
      setDialogOpen(false);
      setApiData(null);
      setErrorToastId(null);
      navigate(`/projects/edit-project/${importedProjectId}`);
    } catch (error) {
      // Dismiss previous error toast if any
      if (errorToastId) {
        toast.dismiss(errorToastId);
      }
      const toastId = showErrorToast(
        error instanceof Error ? error.message : "Failed to import project",
        { persistent: true, width: "700px", showCloseButton: true }
      );
      setErrorToastId(toastId);
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <>
      <div className="px-2 py-5 flex flex-wrap gap-5 w-full items-center justify-between">
        <div className="relative w-fit">
          <Input
            placeholder="Search Projects..."
            value={serverPagination ? searchValue : (table.getColumn(isDistribution ? "project_name" : "projectName")?.getFilterValue() as string) ?? ""}
            onChange={(event) => {
              if (serverPagination && onSearchChange) {
                onSearchChange(event.target.value);
              } else {
                const columnName = isDistribution ? "project_name" : "projectName";
                table.getColumn(columnName)?.setFilterValue(event.target.value);
              }
            }}
            className="h-8 w-5/6 lg:w-[400px] pl-8 rounded-sm border border-r-1"
          />
          <Search
            className="absolute left-2 top-2 text-border-style-1"
            size={16}
          />
        </div>

        <div className="flex items-center gap-4">
          {!isDistribution && !isOffline && <button  className='button-style bg-blue text-white hover:bg-blue/90 flex-center px-4 h-8 rounded-md semi-bold-16 text-sm' onClick={() => setDialogOpen(true)}>Import from 3DX</button>}

          <Dialog open={dialogOpen} onOpenChange={() => {
            setDialogOpen(false);
            setApiData(null);
            setModalSearchValue("");
          }}>
            <DialogContent
              className="width-700 max-w-[80%] max-h-[80%]  p-6 z-[300] overflow-y-auto"
              style={showImportConfirm ? { opacity: 0.8 } : undefined}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-[24px]">
                  Import Project from 3DX to DCS
                </DialogTitle>
              </DialogHeader> 

              <ModalContent 
                modalSearchValue= {modalSearchValue} 
                setModalSearchValue = {setModalSearchValue}  
                apiData={apiData} 
                setApiData={setApiData}/>

              <ConfirmDialog
                open={showImportConfirm}
                onOpenChange={(open) => {
                  setShowImportConfirm(open);
                  if (!open && errorToastId) {
                    toast.dismiss(errorToastId);
                    setErrorToastId(null);
                  }
                }}
                onConfirm={handleImportButtonClick}
                isLoading={isImporting}
              />
              
              <DialogFooter className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setApiData(null);
                    setModalSearchValue("");
                  }}
                >
                  Cancel
                </Button>

                
                <Button
                  onClick={() => setShowImportConfirm(true)}
                  disabled={importedProject?.dtrStatus?.toLowerCase() !== 'active' || !modalSearchValue || !apiData || apiData?.data.length === 0}
                  className="bg-blue text-white hover:bg-[#003B7D]"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {user?.role !== 'Contractor' && !isOffline && !isDistribution && <Link
            onClick={handleCreateProject}
            to="#"
            className="button-style bg-blue text-white hover:bg-blue/90 flex-center px-4 h-8 rounded-md semi-bold-16 text-sm"
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <span>
                <Plus size={20} />
              </span>
            )}
            &nbsp; <span className="hidden sm:block">Create</span> &nbsp;
            <span>Project</span>
            &nbsp;
          </Link>}
          {user?.role !== 'Contractor' && !isOffline && isDistribution && <Link
            to="/distribution-analytics/new-project"
            className="button-style flex-center px-4 h-8 rounded-md semi-bold-16 text-sm"
          >
            Create Project
          </Link>}
          <DataTableViewOptions table={table} />

          <div className="flex items-center space-x-2">
            {!serverPagination && (
              <div className="flex items-center space-x-2">
                <Select
                  value={`${table.getState().pagination.pageSize}`}
                  onValueChange={(value) => {
                    table.setPageSize(Number(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={table.getState().pagination.pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <SelectItem key={pageSize} value={`${pageSize}`}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {!serverPagination && (
              <>
                <Button
                  variant="ghost"
                  className="p-0 text-blue"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <span className="sr-only">Go to first page</span>
                  <ChevronFirst className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  className="p-0 text-blue"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  className="p-0 text-blue"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  className="p-0 flex text-blue"
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <span className="sr-only">Go to last page</span>
                  <ChevronLast className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-500 font-bold">Please have these reference documents before you start</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <ul className="list-disc pl-6 space-y-2">
              <li>Primary pkg drawing/spec for each variable</li>
              <li>Secondary/tertiary pkg drawing/spec for each variable</li>
              <li>Unit load diagram (tops/cape) for each variable</li>
            </ul>
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCreate}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
